import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { MotionGlobalConfig } from 'framer-motion';
import { setupLinkTracking } from 'utils/linkTracking';
import { isPrerendering } from 'utils/prerender';

import App from './app/App';
import reportWebVitals from './reportWebVitals';

/**
 * While scripts/prerender.js captures the page, framer-motion's frame clock is
 * held at 0 so every animation stays at its `initial` keyframe. The prerendered
 * HTML then contains exactly the styles React produces on its first client
 * render (hydration matches) and entrance animations start from that hidden
 * state instead of content flashing visible -> hidden -> revealed.
 */
if (isPrerendering()) {
  MotionGlobalConfig.useManualTiming = true;
}

/**
 * Calendly links open the booking overlay and completed bookings are sent as
 * conversions (utils/linkTracking.js). Registered before React renders so the
 * prerendered "Termin vereinbaren" buttons are covered as soon as this bundle
 * runs. Not while prerendering: nobody clicks there.
 */
if (!isPrerendering()) {
  setupLinkTracking();
}

/**
 * scripts/prerender.js bakes the styled-components CSS into a temporary
 * <style data-prerender-css> tag so the prerendered HTML is styled before JS
 * runs. Once React has committed, styled-components has injected its own
 * rules and the temporary tag can go.
 */
const RemovePrerenderStyles = () => {
  useEffect(() => {
    document
      .querySelectorAll('style[data-prerender-css]')
      .forEach((el) => el.remove());
  }, []);
  return null;
};

const container = document.getElementById('root');
const app = (
  <React.StrictMode>
    <App />
    <RemovePrerenderStyles />
  </React.StrictMode>
);

const normalizePath = (path) => (path || '/').replace(/\/+$/, '') || '/';
const prerenderedPath = container.getAttribute('data-prerender-path');
const prerenderedForThisRoute =
  container.hasChildNodes() &&
  normalizePath(prerenderedPath) === normalizePath(window.location.pathname);

if (prerenderedForThisRoute) {
  // Markup was prerendered at build time (scripts/prerender.js): hydrate it.
  // Should the markup ever mismatch, React falls back to a client render, so
  // such errors are recoverable and intentionally not logged.
  ReactDOM.hydrateRoot(container, app, { onRecoverableError: () => {} });
} else {
  // Either a plain SPA build, or the host served a page prerendered for a
  // different route (SPA fallback rewrite): start from an empty container.
  container.innerHTML = '';
  ReactDOM.createRoot(container).render(app);
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
